/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosAsset,
	CosmosCard,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
} from "@components/elements";
import type { CosmosIconsType } from "@components/elements/atoms/eds-icon/EdsIcon.atom";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { SpeakerCardFragment } from "@cosmos/hygraph-schema";

const svgStyles = css({
	marginBottom: "auto",
	smDown: {
		marginBottom: "initial",
	},
});

const cardStyles = css({
	height: "100%",
	maxW: "100%",
	overflow: "hidden",
});

const contentStyles = css({
	alignItems: "start",
	display: "flex",
	flexDirection: "column",
	justifyContent: "start",
	gap: "eds.dimension.8",
	minW: "0",
	height: "100%",
	maxW: "100%",
	minHeight: "150px",
	smDown: {
		height: "auto",
		minHeight: "100px",
	},
});

const thumbnailStyles = css({
	maxWidth: "270px",
	height: "auto",
	alignSelf: "stretch",
	aspectRatio: "16/9",
	borderRadius: "eds.borderRadius.12",
	flexShrink: "0",
	smDown: {
		maxWidth: "100%",
		aspectRatio: "2/1",
	},
});

const assetStyles = css({
	height: "100%",
	alignSelf: "stretch",
	objectFit: "cover",
});

export function SpeakerCard({ icon, asset, title, company, desc }: SpeakerCardFragment): React.JSX.Element {
	return (
		<CosmosCard
			className={cx("speaker-card", cardStyles)}
			padding={12}
			variant="material"
			direction="delphinusDir"
			assetPosition="start"
		>
			<CosmosCard.Content className={cx("speaker-card-content", contentStyles)} padding={8}>
				<VStack gap="eds.dimension.8" alignItems="flex-start" justifyContent="center" height="full">
					{icon ? <EdsIcon icon={icon as CosmosIconsType} size="lg" color="primary" className={svgStyles} /> : null}
					<VStack gap="eds.dimension.12" alignItems="start">
						{title ? (
							<CosmosText variant={CosmosTextVariantEnum.headingMD} as="h3" color="eds.text.contrast">
								{title}
							</CosmosText>
						) : null}
						<VStack gap="eds.dimension.8" alignItems="start">
							{company ? (
								<CosmosText variant={CosmosTextVariantEnum.eyebrowSM} color="eds.text.primary">
									{company}
								</CosmosText>
							) : null}
							{desc ? (
								<CosmosRichText variant={CosmosTextVariantEnum.uiSM} color="eds.text.secondary" html={desc} />
							) : null}
						</VStack>
					</VStack>
				</VStack>
			</CosmosCard.Content>
			{asset ? (
				<CosmosCard.Thumbnail className={thumbnailStyles} aspectRatio="unset">
					<CosmosAsset asset={asset} className={assetStyles} />
				</CosmosCard.Thumbnail>
			) : null}
		</CosmosCard>
	);
}
